/**
 * borrowed/stripped down version of dashboard/views/DashboardEditorStartPageView/DashboardEditorStartPageView.tsx
 */
import { Suspense, useMemo } from 'react';

import { firstInitialStep } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import LocalizedDirection from 'dashboard/components/LocalizedDirection';
import ThemeEditorNavBack from 'dashboard/components/ThemeEditorNavBack';
import FormThemeEditorStartPageLogo from 'dashboard/forms/FormThemeEditorStartPageLogo';
import { InquiryTemplateVersion, isUiStepConfig } from 'dashboard/models/inquiry-template-version';
import { headerComponentIndex } from 'dashboard/views/DashboardInquiryTemplatesShowView/utils';
import { StepPreview, StepUi } from 'lib/interfaces/StepsConfig';
import { UiComponentType } from 'lib/interfaces/ui-components';

interface DashboardEditorStartLogoViewProps {
  stepsPreview: StepPreview[];
  inqTmplVersionPristine: InquiryTemplateVersion;
  disabled?: boolean;
  handleSave: () => void;
  reactRouterV6?: boolean;
}

const DashboardEditorStartLogoView = ({
  inqTmplVersionPristine,
  stepsPreview,
  disabled,
  handleSave,
  reactRouterV6,
}: DashboardEditorStartLogoViewProps) => {
  const initialValues = useMemo(
    () => ({
      inqTmplVersion: inqTmplVersionPristine.attributes,
    }),
    [inqTmplVersionPristine],
  );

  // steps client data can be null or empty while loading
  if (stepsPreview == null || stepsPreview.length === 0) {
    return null;
  }

  const { steps } = inqTmplVersionPristine.attributes;
  const initialStep = firstInitialStep(steps);
  // return if initial step is null or non-UI step (no components)
  if (!isUiStepConfig(initialStep)) {
    return null;
  }

  const componentIndex = headerComponentIndex(initialStep.config.components);
  if (componentIndex === -1) {
    return null;
  }

  const startIndex = steps.findIndex((step) => step.name === initialStep.name);
  const name = `inqTmplVersion.steps.${startIndex}.config.components.${componentIndex}`;
  const startStepPreview = stepsPreview.find(
    (stepPreview) => stepPreview.name === initialStep.name,
  ) as StepUi;
  const headerComponentPreview = startStepPreview.config.components[componentIndex];

  let currentLogoUrl = null;
  let titleTextPreview = null;

  switch (headerComponentPreview.type) {
    case UiComponentType.ImageRemote:
      currentLogoUrl = headerComponentPreview.attributes.url;
      break;
    case UiComponentType.Title:
      titleTextPreview = headerComponentPreview.attributes.text;
      break;
  }

  return (
    <Suspense fallback={null}>
      <LocalizedDirection>
        <ThemeEditorNavBack reactRouterV6={reactRouterV6} />
        <FormThemeEditorStartPageLogo
          name={name}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          previewPointer={{ currentLogoUrl, titleTextPreview }}
          initialValues={initialValues}
          keepDirtyOnReinitialize={false}
          disabled={disabled}
          onSubmit={handleSave}
        />
      </LocalizedDirection>
    </Suspense>
  );
};

export default DashboardEditorStartLogoView;
